import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, MoreVertical, Eye, Edit, Trash2, 
  Image as ImageIcon, Layers, Share2, AlertCircle, CheckCircle, XCircle, Upload,
  LayoutGrid, List, ChevronUp, ChevronDown, TrendingUp, TrendingDown,
  ArrowUpDown, RefreshCw, Shirt, ExternalLink, Copy, BarChart2,
  ShoppingCart, Clock, Sparkles, ChevronLeft, ChevronRight, Link2
} from 'lucide-react';
import { inventoryAPI } from '../../api';
import toast from 'react-hot-toast';
import UpgradePrompt from '../../components/UpgradePrompt';
import { InventoryFilters, useInventoryFilters } from '../../components/filters';
import { ColorVariantsSwatchRow } from '../../components/ColorVariantsEditor';
import ImportFromUrlModal from './ImportFromUrlModal';

// Moment display names
const MOMENT_NAMES = {
  creative_workday: 'Creative Workday',
  unwind: 'Unwind',
  after_hours: 'After Hours',
  escape: 'The Escape',
  gentle_celebration: 'Gentle Celebration'
};

const MOMENT_COLORS = {
  creative_workday: 'bg-blue-100 text-blue-700',
  unwind: 'bg-green-100 text-green-700',
  after_hours: 'bg-purple-100 text-purple-700',
  escape: 'bg-cyan-100 text-cyan-700',
  gentle_celebration: 'bg-pink-100 text-pink-700'
};

// Stats Card Component
const StatsCard = ({ icon: Icon, title, value, subtitle, trend, className = '' }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-4 ${className}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{title}</p>
          <p className="text-xl font-bold text-slate-900">{value}</p>
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
  </div>
);

// Product Card Component (Grid View)
const ProductCard = ({ product, onAction, dropdownOpen, setDropdownOpen }) => {
  const getStatusBadge = (status) => {
    if (status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
        <XCircle className="w-3 h-3" />
        Inactive
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow group"
    >
      {/* Image - use thumbnail for faster loading if available */}
      <div className="aspect-square bg-slate-100 relative">
        {(product.tryon_input_thumbnail_url || product.tryon_input_image_url) ? (
          <img
            src={product.tryon_input_thumbnail_url || product.tryon_input_image_url}
            alt={product.product_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
            <ImageIcon className="w-10 h-10 mb-2" />
            <span className="text-xs">No image</span>
          </div>
        )}
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          {getStatusBadge(product.status)}
        </div>
        {/* Quick Actions Overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Link
            to={`/partners/inventory/${product.product_id}`}
            className="p-2 bg-white rounded-full hover:bg-blue-50 transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-slate-700" />
          </Link>
          <Link
            to={`/partners/inventory/${product.product_id}/images`}
            className="p-2 bg-white rounded-full hover:bg-blue-50 transition-colors"
            title="Manage Images"
          >
            <ImageIcon className="w-4 h-4 text-slate-700" />
          </Link>
          <Link
            to={`/partners/inventory/${product.product_id}/share-links`}
            className="p-2 bg-white rounded-full hover:bg-blue-50 transition-colors"
            title="Share Links"
          >
            <Share2 className="w-4 h-4 text-slate-700" />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate text-sm sm:text-base">{product.product_name}</h3>
            <p className="text-xs sm:text-sm text-slate-500 truncate">SKU: {product.sku_id}</p>
          </div>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(dropdownOpen === product.product_id ? null : product.product_id)}
              className="p-1 text-slate-400 hover:text-slate-600 rounded"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {dropdownOpen === product.product_id && (
              <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
                <Link
                  to={`/partners/inventory/${product.product_id}`}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Eye className="w-4 h-4" /> View Details
                </Link>
                <Link
                  to={`/partners/inventory/${product.product_id}/images`}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <ImageIcon className="w-4 h-4" /> Manage Images
                </Link>
                <Link
                  to={`/partners/inventory/${product.product_id}/variants`}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Layers className="w-4 h-4" /> Manage Variants
                </Link>
                <Link
                  to={`/partners/inventory/${product.product_id}/share-links`}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Share2 className="w-4 h-4" /> Share Links
                </Link>
                <hr className="my-1" />
                <button
                  onClick={() => onAction('delete', product)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            {product.image_count}
          </span>
          <span className="inline-flex items-center gap-1">
            <Layers className="w-3 h-3" />
            {product.variant_count}
          </span>
          <span className="text-slate-300">|</span>
          <span className={product.total_stock > 0 ? 'text-green-600' : 'text-red-500'}>
            {product.total_stock} in stock
          </span>
        </div>

        {/* Moment Tag + Color Variants */}
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            MOMENT_COLORS[product.assigned_moment] || 'bg-slate-100 text-slate-700'
          }`}>
            {MOMENT_NAMES[product.assigned_moment] || product.assigned_moment}
          </span>
          {(product.color_variants || []).length > 0 && (
            <div className="flex items-center gap-1.5">
              <ColorVariantsSwatchRow variants={product.color_variants} max={4} size="sm" />
              {product.color_change_enabled && (
                <span
                  title={`AI Color Change · ${product.color_change_quality || 'standard'}`}
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 text-white"
                  data-testid="product-card-color-change-badge"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Warning */}
        {!product.tryon_input_image_key && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            <AlertCircle className="w-3 h-3" />
            No Try-On image
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Product Row Component (Table View) - Enhanced with price and tryon toggle
const ProductRow = ({ product, onAction, isSelected, onSelect, onToggleTryOn }) => {
  const [toggling, setToggling] = useState(false);
  
  const handleToggleTryOn = async () => {
    setToggling(true);
    try {
      await onToggleTryOn(product);
    } finally {
      setToggling(false);
    }
  };

  return (
    <tr className="hover:bg-slate-50 border-b border-slate-100 last:border-0">
      <td className="py-2.5 px-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="py-2.5 px-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
            {(product.tryon_input_thumbnail_url || product.tryon_input_image_url) ? (
              <img
                src={product.tryon_input_thumbnail_url || product.tryon_input_image_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <Link 
              to={`/partners/inventory/${product.product_id}`}
              className="font-medium text-slate-900 hover:text-blue-600 truncate block max-w-[180px] text-sm"
            >
              {product.product_name}
            </Link>
            <p className="text-xs text-slate-500 font-mono truncate max-w-[180px]">{product.sku_id}</p>
          </div>
        </div>
      </td>
      <td className="py-2.5 px-3">
        <span className="text-xs text-slate-500">{product.category || '-'}</span>
      </td>
      <td className="py-2.5 px-3">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-slate-900">
            ₹{(product.selling_price || product.price || 0).toLocaleString()}
          </span>
          {product.mrp && product.mrp > (product.selling_price || product.price) && (
            <span className="text-xs text-slate-400 line-through">
              ₹{product.mrp.toLocaleString()}
            </span>
          )}
        </div>
      </td>
      <td className="py-2.5 px-3 text-center">
        <span className={`text-sm font-medium ${product.total_stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
          {product.total_stock || 0}
        </span>
      </td>
      <td className="py-2.5 px-3 text-center">
        {(product.color_variants || []).length > 0 ? (
          <div className="flex items-center justify-center gap-1.5">
            <ColorVariantsSwatchRow variants={product.color_variants} max={4} size="sm" />
            {product.color_change_enabled && (
              <span
                title={`AI Color Change · ${product.color_change_quality || 'standard'}`}
                className="inline-flex items-center text-[10px] font-semibold px-1 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 text-white"
                data-testid="product-row-color-change-badge"
              >
                <Sparkles className="w-2.5 h-2.5" />
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-slate-500">{product.color || '-'}</span>
        )}
      </td>
      <td className="py-2.5 px-3 text-center">
        {/* TryOn Toggle */}
        <button
          onClick={handleToggleTryOn}
          disabled={toggling || !product.tryon_input_image_key}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
            product.status === 'ACTIVE' 
              ? 'bg-green-500' 
              : 'bg-slate-300'
          } ${(!product.tryon_input_image_key || toggling) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          title={!product.tryon_input_image_key ? 'Upload try-on image first' : (product.status === 'ACTIVE' ? 'Disable TryOn' : 'Enable TryOn')}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              product.status === 'ACTIVE' ? 'translate-x-4.5' : 'translate-x-1'
            }`}
            style={{ transform: product.status === 'ACTIVE' ? 'translateX(18px)' : 'translateX(2px)' }}
          />
        </button>
      </td>
      <td className="py-2.5 px-2">
        <div className="flex items-center gap-0.5 justify-end">
          <Link
            to={`/partners/inventory/${product.product_id}`}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="View/Edit"
          >
            <Eye className="w-4 h-4" />
          </Link>
          <Link
            to={`/partners/inventory/${product.product_id}/images`}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Manage Images"
          >
            <ImageIcon className="w-4 h-4" />
          </Link>
          <Link
            to={`/partners/inventory/${product.product_id}/share-links`}
            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Share Links"
          >
            <Share2 className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onAction('delete', product)}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const Inventory = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [skuLimit, setSkuLimit] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'grid' or 'table' - default to table for faster navigation
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  
  // Status and moment filters (server-side)
  const [statusFilter, setStatusFilter] = useState('');
  const [momentFilter, setMomentFilter] = useState('');
  
  // Use the reusable filter hook for client-side filtering with default gender filter
  const {
    searchQuery,
    setSearchQuery,
    filters,
    updateFilter,
    updatePriceRange,
    clearFilters,
    filterOptions,
    filteredProducts: clientFilteredProducts,
    activeFilterCount
  } = useInventoryFilters(products, 'female');

  // Pagination: 10 items for table view, 20 for grid view
  const pageSize = viewMode === 'table' ? 10 : 20;

  useEffect(() => {
    fetchProducts();
    fetchSkuLimit();
  }, [page, statusFilter, momentFilter, pageSize]);

  const fetchSkuLimit = async () => {
    try {
      const response = await inventoryAPI.checkSkuLimit();
      setSkuLimit(response.data);
    } catch (error) {
      console.error('SKU limit check failed:', error);
    }
  };

  const fetchProducts = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const params = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;
      
      const response = await inventoryAPI.listProducts(params);
      let items = response.data.products;
      
      // Client-side moment filter
      if (momentFilter) {
        items = items.filter(p => p.assigned_moment === momentFilter);
      }
      
      // Client-side sorting
      items.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        if (sortBy === 'total_stock' || sortBy === 'image_count' || sortBy === 'variant_count') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setProducts(items);
      setTotalProducts(response.data.total);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load products');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Apply client-side filters (search + advanced filters)
  const displayProducts = useMemo(() => {
    return clientFilteredProducts;
  }, [clientFilteredProducts]);

  const handleAction = async (action, product) => {
    if (action === 'delete') {
      if (!window.confirm(`Delete "${product.product_name}"? This will remove all images, variants, and share links.`)) {
        return;
      }
      try {
        await inventoryAPI.deleteProduct(product.product_id);
        toast.success('Product deleted');
        fetchProducts();
        fetchSkuLimit();
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
    setDropdownOpen(null);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === displayProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(displayProducts.map(p => p.product_id));
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleToggleTryOn = async (product) => {
    try {
      const newStatus = product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await inventoryAPI.updateProduct(product.product_id, { status: newStatus });
      toast.success(`TryOn ${newStatus === 'ACTIVE' ? 'enabled' : 'disabled'} for ${product.product_name}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const active = products.filter(p => p.status === 'ACTIVE').length;
    const inactive = products.filter(p => p.status === 'INACTIVE').length;
    const totalStock = products.reduce((sum, p) => sum + (p.total_stock || 0), 0);
    const noImage = products.filter(p => !p.tryon_input_image_key).length;
    return { active, inactive, totalStock, noImage };
  }, [products]);

  // Show upgrade prompt if access is denied
  if (accessDenied) {
    return (
      <UpgradePrompt
        feature="Inventory Management"
        requiredPlan="Growth"
        description="Manage your product catalog with SKU-based try-on links, image galleries, and variants."
        icon={Package}
      />
    );
  }

  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Inventory</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Manage your product catalog for Virtual Try-On
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* SKU Limit Indicator */}
            {skuLimit && (
              <div className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium ${
                !skuLimit.can_add_more 
                  ? 'bg-red-100 text-red-700' 
                  : skuLimit.remaining !== 'unlimited' && skuLimit.remaining <= 3
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-600'
              }`}>
                {skuLimit.remaining === 'unlimited' 
                  ? `${skuLimit.current_count} SKUs`
                  : `${skuLimit.current_count}/${skuLimit.max_allowed} SKUs`}
              </div>
            )}
            <button
              onClick={() => fetchProducts(true)}
              disabled={refreshing}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/partners/inventory/bulk-upload')}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
            <button
              onClick={() => setImportModalOpen(true)}
              disabled={skuLimit && !skuLimit.can_add_more}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-blue-200 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="import-from-url-btn"
            >
              <Link2 className="w-4 h-4" />
              <span className="hidden sm:inline">Import from URL</span>
              <span className="sm:hidden">URL</span>
            </button>
            <button
              onClick={() => navigate('/partners/inventory/new')}
              disabled={skuLimit && !skuLimit.can_add_more}
              className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Product</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* SKU Limit Warning */}
        {skuLimit && !skuLimit.can_add_more && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 text-sm">SKU limit reached</p>
              <p className="text-xs sm:text-sm text-amber-600 mt-0.5">
                You have reached your plan limit of {skuLimit.max_allowed} SKUs. 
                <Link to="/partners/subscription" className="ml-1 underline hover:no-underline">
                  Upgrade your plan
                </Link> to add more products.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {totalProducts > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatsCard
            icon={Package}
            title="Total Products"
            value={totalProducts}
          />
          <StatsCard
            icon={CheckCircle}
            title="Active"
            value={stats.active}
            subtitle={`${stats.inactive} inactive`}
          />
          <StatsCard
            icon={ShoppingCart}
            title="Total Stock"
            value={stats.totalStock.toLocaleString()}
          />
          <StatsCard
            icon={AlertCircle}
            title="Missing Image"
            value={stats.noImage}
            subtitle="Need try-on image"
          />
        </div>
      )}

      {/* Filters & View Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          {/* Primary Filter Row - Using Unified Component */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <InventoryFilters
              variant="compact"
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={updateFilter}
              onSearchChange={setSearchQuery}
              onPriceRangeChange={updatePriceRange}
              onReset={clearFilters}
              searchQuery={searchQuery}
              activeFilterCount={activeFilterCount}
              showFilters={['gender', 'search', 'category', 'color', 'stock', 'price']}
              searchPlaceholder="Search by name, SKU..."
            />
            
            {/* Additional Controls */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* More Filters Toggle (for Status, Moment) */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                  showFilters || statusFilter || momentFilter
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="expand-filters-btn"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                <span className="hidden sm:inline">More</span>
                {(statusFilter || momentFilter) && (
                  <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                    {(statusFilter ? 1 : 0) + (momentFilter ? 1 : 0)}
                  </span>
                )}
              </button>

              {/* View Toggle */}
              <div className="hidden sm:flex items-center border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  title="Grid View"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                  title="Table View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={() => fetchProducts(true)}
                disabled={refreshing}
                className="hidden sm:flex p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 mt-3 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Inventory-specific Filters</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">All Status</option>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>

                  {/* Moment Filter */}
                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      Moment
                    </label>
                    <select
                      value={momentFilter}
                      onChange={(e) => {
                        setMomentFilter(e.target.value);
                        setPage(1);
                      }}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      <option value="">All Moments</option>
                      {Object.entries(MOMENT_NAMES).map(([key, name]) => (
                        <option key={key} value={key}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Clear Filters & View Toggle (Mobile) */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <div className="sm:hidden flex items-center gap-2">
                    <span className="text-xs text-slate-500">View:</span>
                    <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}
                      >
                        <LayoutGrid className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('table')}
                        className={`p-2 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-slate-500'}`}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {(statusFilter || momentFilter) && (
                    <button
                      onClick={() => {
                        setStatusFilter('');
                        setMomentFilter('');
                        setPage(1);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Clear inventory filters
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Products Display */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : displayProducts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-8 sm:p-12 text-center"
        >
          <Package className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            {products.length === 0 ? 'No products yet' : 'No matching products'}
          </h3>
          <p className="text-slate-500 mb-6 text-sm">
            {products.length === 0 
              ? 'Add your first product to start creating shareable Try-On links'
              : 'Try adjusting your search or filters'}
          </p>
          {products.length === 0 ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/partners/inventory/new')}
                disabled={skuLimit && !skuLimit.can_add_more}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300"
              >
                <Plus className="w-5 h-5" />
                Add Product
              </button>
              <button
                onClick={() => navigate('/partners/inventory/bulk-upload')}
                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                <Upload className="w-5 h-5" />
                Bulk Upload
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                clearFilters();
                setStatusFilter('');
                setMomentFilter('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear all filters
            </button>
          )}
        </motion.div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {displayProducts.map((product) => (
            <ProductCard
              key={product.product_id}
              product={product}
              onAction={handleAction}
              dropdownOpen={dropdownOpen}
              setDropdownOpen={setDropdownOpen}
            />
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedProducts.length === products.length && products.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className="py-3 px-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('product_name')}
                  >
                    <div className="flex items-center gap-1">
                      Product
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Category
                  </th>
                  <th 
                    className="py-3 px-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('selling_price')}
                  >
                    <div className="flex items-center gap-1">
                      Price
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th 
                    className="py-3 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                    onClick={() => handleSort('total_stock')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Stock
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Color
                  </th>
                  <th className="py-3 px-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    TryOn
                  </th>
                  <th className="py-3 px-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayProducts.map((product) => (
                  <ProductRow
                    key={product.product_id}
                    product={product}
                    onAction={handleAction}
                    isSelected={selectedProducts.includes(product.product_id)}
                    onSelect={() => handleSelectProduct(product.product_id)}
                    onToggleTryOn={handleToggleTryOn}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalProducts > pageSize && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-slate-500">
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalProducts)} of {totalProducts}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-xs"
            >
              First
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-2 text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * pageSize >= totalProducts}
              className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="p-2 border border-slate-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 text-xs"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Helper Tips */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 text-sm mb-1">💡 Pro Tips</p>
            <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
              <li>• Upload multiple images for each product and select one as the Try-On input</li>
              <li>• Create share links to let customers try products before buying</li>
              <li>• Use table view for faster navigation when managing many products</li>
            </ul>
          </div>
        </div>
      </div>

      <ImportFromUrlModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onComplete={() => fetchProducts(true)}
      />
    </div>
  );
};

export default Inventory;
